import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TableSortLabel,
} from '@mui/material'

export interface ColumnDef<T = any> {
  id: string
  label: string
  align?: 'left' | 'right' | 'center'
  width?: string
  render?: (row: T) => React.ReactNode
  sortable?: boolean
}

interface Props<T> {
  columns: ColumnDef<T>[]
  rows: T[]
  getRowKey: (row: T) => string | number
  emptyMessage?: string
  orderBy?: string | null
  order?: 'asc' | 'desc'
  onRequestSort?: (columnId: string) => void
  // Optional extra renderer inserted after each row (useful to render
  // expansion rows or additional sibling rows per item). If provided,
  // the returned node will be rendered directly after the main TableRow
  // for that item.
  rowExtra?: (row: T) => React.ReactNode
  // Optional row style override per-row. Return a CSSProperties object to
  // apply inline styles to the main TableRow for that row.
  rowStyle?: (row: T) => React.CSSProperties | undefined
}

export default function InventoryTable<T>(props: Props<T>) {
  const {
    columns,
    rows,
    getRowKey,
    emptyMessage,
    orderBy,
    order = 'asc',
    onRequestSort,
  } = props
  const { rowExtra } = props
  const { rowStyle } = props

  if (!rows || rows.length === 0) {
    return (
      <Paper>
        <Box p={2}>
          <Typography>{emptyMessage ?? 'Tidak ada data.'}</Typography>
        </Box>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align ?? 'left'}
                sortDirection={orderBy === col.id ? order : false}
              >
                {col.sortable && onRequestSort ? (
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => onRequestSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <React.Fragment key={String(getRowKey(row))}>
              <TableRow
                hover
                style={{
                  transition: 'background-color 200ms ease',
                  ...(rowStyle ? rowStyle(row) : {}),
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align ?? 'left'}>
                    {col.render ? col.render(row) : (row as any)[col.id]}
                  </TableCell>
                ))}
              </TableRow>
              {rowExtra && rowExtra(row)}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
